/**
 * explorerCellShape — the single source of truth for turning an /explorer/v2
 * list item into the nested cell shape the Explorer plots/grid/filters consume
 * (`cell.electrical.*`, `cell.mechanical.*`, `cell.chemical.*`, …).
 *
 * Extracted from MainAppPage's inline `transformItems` so the BATEMO ION chat
 * plot (SalesRagonePlot) can seed the SAME full catalogue in the SAME shape as
 * the Explorer Ragone, without re-implementing (and drifting from) the mapping.
 * Values are already in display units from the explorer endpoint.
 *
 * DEMO metric-tiering (demo-explorer-metric-tiering):
 *   For DEMO users the backend keeps base performance exact but withholds a set
 *   of premium measured metrics. The response then carries:
 *     - `gated_fields: string[]` — backend field keys withheld for this row
 *       (empty for PRO/admin/internal → nothing is tiered).
 *     - coarse teaser bands `batch_variation_band` / `aging_band`
 *       ("low"|"medium"|"high"|null) while the exact pct stays null.
 *   The renderers must draw a typed lock/teaser state for those — never a bare
 *   "—" (which reads as "Batemo has no data"). We translate `gated_fields`
 *   (backend keys) into `cell._gated` (a Set of CELL-SHAPE keys) via the SAME
 *   map used for the values below, so the gate stays automatically in sync.
 */

/**
 * Backend explorer field key → cell-shape key (the dotted path a renderer reads,
 * e.g. `electrical.R_dc_mOhm`). This is the reverse of the value mapping in
 * `transformExplorerItem` and the single source of truth for translating the
 * backend `gated_fields` list into `cell._gated`. Keep an entry here for every
 * premium metric the backend may gate; unknown keys are stored verbatim as a
 * best-effort fallback so a newly-gated backend field still renders as locked.
 */
export const EXPLORER_KEY_TO_CELL_SHAPE_KEY = {
  // Resistance / impedance (mΩ)
  r_dc_mohm: 'electrical.R_dc_mOhm',
  r_eis_mohm: 'electrical.R_EIS_mOhm',
  r_ac_mohm: 'electrical.R_ac_mOhm',
  r_pol_mohm: 'electrical.R_pol_25',
  // Efficiency
  eta_dc_pct: 'electrical.eta_dc_pct',
  // DC25 performance
  p_dc_25_w: 'electrical.P_dc_25_W',
  w_dc_25_wh: 'electrical.W_dc_25_Wh',
  i_dc_25_a: 'electrical.I_dc_25_A',
  t_max_dc_25_s: 'electrical.t_max_dc_25', // duration to T_max (s)
  // Thermal
  delta_t_dc_25_k: 'thermal.delta_T_dc_25',
  t_max_dc_25_c: 'thermal.T_max_dc_25',
  p_loss_dc_25_w: 'thermal.P_loss_dc_25',
  w_loss_dc_25_wh: 'thermal.W_loss_dc_25',
  // Quality (exact pct is withheld; a coarse band is served instead)
  batch_variation_pct: 'batch.CAP_spread_std_rel',
  batch_eis_variation_pct: 'batch.EIS_spread_std_rel',
  aging_pct: 'aging.CAP_loss_rel',
  aging_eis_pct: 'aging.EIS_increase_rel',
}

/**
 * Translate the backend `gated_fields` list into a Set of cell-shape keys.
 * Unknown backend keys are kept verbatim (best-effort) so a field the backend
 * starts gating tomorrow still renders locked instead of leaking a bare "—".
 * @param {string[]} [gatedFields]
 * @returns {Set<string>}
 */
export function gatedFieldsToCellShapeKeys(gatedFields) {
  const set = new Set()
  if (!Array.isArray(gatedFields)) return set
  for (const key of gatedFields) {
    if (!key) continue
    const mapped = EXPLORER_KEY_TO_CELL_SHAPE_KEY[key]
    if (mapped) set.add(mapped)
    // Also store the raw backend key so callers can match on either form.
    set.add(key)
  }
  return set
}

/**
 * Re-key a raw `binned_metrics` map (backend explorer field key → band-center
 * value, demo scraping-friction Task 5) to CELL-SHAPE keys via the SAME map
 * used above, so a renderer can read `cell.binned[kpi.field]` with the exact
 * key shape `getKPIValue`/KPI `.field` paths use. Unknown backend keys are
 * kept verbatim (best-effort), mirroring `gatedFieldsToCellShapeKeys`.
 * @param {Record<string, number>} [binnedMetrics]
 * @returns {Record<string, number>}
 */
export function remapBinned(binnedMetrics) {
  const out = {}
  if (!binnedMetrics || typeof binnedMetrics !== 'object') return out
  for (const [key, value] of Object.entries(binnedMetrics)) {
    if (value == null) continue
    const mapped = EXPLORER_KEY_TO_CELL_SHAPE_KEY[key]
    out[mapped || key] = value
  }
  return out
}

/** Transform one explorer list item to the nested display-shape cell. */
export function transformExplorerItem(item) {
  return {
    id: item.product_id,
    product_id: item.product_id,
    name: item.name,
    manufacturer: item.manufacturer,
    country: item.country,
    year: item.year,
    date: item.date || (item.year ? `${item.year}-01-01` : null), // actual date, fallback to year
    // When BATEMO ADDED the cell to the catalogue — NOT the cell's date of
    // introduction above. Deliberately no fallback to `date`/`year`: the two are
    // different facts (a cell released in 2019 can have been added last week),
    // and quietly substituting one would make "the newest cells" answer a
    // question nobody asked, with no way to tell. Read by
    // `utils/cellSort.js#addedDateSortValue` for the `addedDate` sort.
    createdAt: item.created_at || null,
    technology: item.technology,
    is_demo: item.is_demo || false,
    is_locked: item.is_locked || false,
    thumbnail_url: item.thumbnail_url,

    // Demo scraping-friction (Task 5/11): opaque per-(user,cell,day) unlock
    // handle + fixed-granularity binned band centers for this row's gated
    // fields, re-keyed to cell-shape keys so FreePlot can read
    // `cell.binned[kpi.field]` next to `getKPIValue`. Both null/empty for
    // unlocked / exempt / PRO rows (nothing gated for those).
    handle: item.handle ?? null,
    binned: remapBinned(item.binned_metrics),

    // Nested objects for compatibility with existing GridView/filtering
    chemical: {
      main_chemistry: item.main_chemistry || item.cathode_chemistry || item.technology,
      cathode_material_group: item.cathode_chemistry || item.main_chemistry,
      anode_material_group: item.anode_chemistry,
      cathode: item.cathode_chemistry || item.main_chemistry,
      anode: item.anode_chemistry,
    },

    mechanical: {
      format: item.format,
      format_group: item.format,
      shape: item.shape,
      m_cell: item.mass_g ? item.mass_g / 1000 : null, // g → kg
      vol_cell: item.volume_cm3 ? item.volume_cm3 / 1e6 : null, // cm³ → m³
      // Also provide display-unit fields for FreePlot KPIs
      mass_g: item.mass_g,
      vol_cell_cm3: item.volume_cm3,
      area_cm2: item.area_cm2,
    },

    // Electrical data from explorer KPIs - ALREADY IN DISPLAY UNITS
    electrical: {
      // Energy values (Wh, Wh/kg, Wh/L)
      E_c10_Wh: item.energy_wh,
      E_grav_Wh_per_kg: item.energy_grav_wh_kg,
      E_vol_Wh_per_L: item.energy_vol_wh_l,

      // Power Peak values (W, W/kg, W/L)
      P_peak_W: item.power_w,
      P_grav_W_per_kg: item.power_grav_w_kg,
      P_vol_W_per_L: item.power_vol_w_l,

      // Capacity values (Ah, Ah/kg, Ah/L)
      C_c10_Ah: item.capacity_ah,
      C_grav_Ah_per_kg: item.capacity_grav_ah_kg,
      C_vol_Ah_per_L: item.capacity_vol_ah_l,

      // Current Peak values (A, A/kg, A/L)
      I_peak_A: item.current_a,
      I_grav_A_per_kg: item.current_grav_a_kg,
      I_vol_A_per_L: item.current_vol_a_l,

      // Continuous current/power
      I_cont_A: item.current_cont_a,
      P_cont_W: item.power_cont_w,

      // Voltage (no fallback for locked cells)
      V_mean: item.voltage_v ?? (item.is_locked ? null : 3.6),

      // DC25 Performance (400 W/kg standard)
      eta_dc_pct: item.eta_dc_pct,
      P_dc_25_W: item.p_dc_25_w,
      W_dc_25_Wh: item.w_dc_25_wh,
      I_dc_25_A: item.i_dc_25_a,
      t_max_dc_25: item.t_max_dc_25_s,

      // Resistance values (mΩ)
      R_dc_mOhm: item.r_dc_mohm,
      R_EIS_mOhm: item.r_eis_mohm,
      R_ac_mOhm: item.r_ac_mohm,
      R_pol_25: item.r_pol_mohm,

      // SI-unit aliases for scoreCalculator compatibility
      // scoreCalculator.js expects these exact names in SI units
      P_peak: item.power_w, // W (same unit)
      W_C10: item.energy_wh != null ? item.energy_wh * 3600 : null, // Wh → J
      C_C10: item.capacity_ah != null ? item.capacity_ah * 3600 : null, // Ah → C
      I_peak: item.current_a, // A (same unit)
      W_C10_grav: item.energy_grav_wh_kg != null ? item.energy_grav_wh_kg * 3600 : null, // Wh/kg → J/kg
      W_C10_vol: item.energy_vol_wh_l != null ? item.energy_vol_wh_l * 3600000 : null, // Wh/L → J/m³
      P_peak_grav: item.power_grav_w_kg, // W/kg (same unit)
      P_peak_vol: item.power_vol_w_l != null ? item.power_vol_w_l * 1000 : null, // W/L → W/m³
    },

    // Thermal data
    thermal: {
      delta_T_dc_25: item.delta_t_dc_25_k,
      T_max_dc_25: item.t_max_dc_25_c,
      P_loss_dc_25: item.p_loss_dc_25_w,
      W_loss_dc_25: item.w_loss_dc_25_wh,
    },

    // Quality metrics — exact pct when present, otherwise a coarse teaser band
    // ("low"|"medium"|"high"|null) the renderer turns into a chip. For DEMO the
    // exact pct stays null and only the band comes through.
    batch: {
      CAP_spread_std_rel: item.batch_variation_pct != null ? item.batch_variation_pct / 100 : null,
      CAP_spread_band: item.batch_variation_band ?? null,
      EIS_spread_std_rel:
        item.batch_eis_variation_pct != null ? item.batch_eis_variation_pct / 100 : null,
    },
    aging: {
      CAP_loss_rel: item.aging_pct != null ? item.aging_pct / 100 : null,
      CAP_loss_band: item.aging_band ?? null,
      EIS_increase_rel: item.aging_eis_pct != null ? item.aging_eis_pct / 100 : null,
    },

    // Per-value gate (DEMO metric-tiering): cell-shape keys whose exact value the
    // backend withheld for this row. Empty for PRO/admin/internal. Renderers key
    // off this (NOT the cell-level is_locked flag) so the cell stays browsable
    // while individual premium values show a lock/teaser token.
    _gated: gatedFieldsToCellShapeKeys(item.gated_fields),
  }
}

/** Transform a list of explorer items. */
export function transformExplorerItems(items) {
  return (Array.isArray(items) ? items : []).map(transformExplorerItem)
}
